/**
 * OracleHistoryBar.tsx - Analysis History Bar
 *
 * Compact horizontal bar showing all Oracle analyses for the current conversation.
 * Allows selecting previous analyses to view their full data.
 */

import { useState, useMemo } from 'react'
import type { OracleResponse } from '@models/oracle'
import './OracleHistoryBar.css'

interface OracleHistoryBarProps {
  responses: OracleResponse[]
  selectedIndex: number
  onSelectEntry: (index: number) => void
}

function formatRelativeTime(_query: string, index: number, total: number): string {
  // Most recent is index 0
  if (index === 0) return 'Latest'
  if (index === total - 1) return 'First'
  return `#${total - index}`
}

function truncateQuery(query: string, maxLen: number = 60): string {
  if (!query) return 'Unknown query'
  if (query.length <= maxLen) return query
  return query.slice(0, maxLen) + '...'
}

function OracleHistoryBar({ responses, selectedIndex, onSelectEntry }: OracleHistoryBarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false)

  const entries = useMemo(() => {
    return responses.map((r, i) => ({
      query: r.query || 'Unknown query',
      confidenceLevel: r.confidence?.level || 'MEDIUM',
      confidenceScore: r.confidence?.score ? Math.round(r.confidence.score * 100) : 0,
      label: formatRelativeTime(r.query, i, responses.length),
    }))
  }, [responses])

  return (
    <div className={`oracle-history-bar ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="history-bar-header" onClick={() => setIsCollapsed(!isCollapsed)}>
        <span className="history-label">Analysis History ({responses.length})</span>
        <button
          className="history-toggle"
          aria-label={isCollapsed ? 'Expand history' : 'Collapse history'}
        >
          {isCollapsed ? '\u25B6' : '\u25BC'}
        </button>
      </div>

      {!isCollapsed && (
        <div className="history-entries">
          {entries.map((entry, index) => (
            <button
              key={index}
              className={`history-entry ${index === selectedIndex ? 'active' : ''}`}
              onClick={() => onSelectEntry(index)}
              title={entry.query}
            >
              <span className="entry-label">{entry.label}</span>
              <span className="entry-query">{truncateQuery(entry.query)}</span>
              <span className={`entry-confidence ${entry.confidenceLevel.toLowerCase()}`}>
                {entry.confidenceLevel}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export default OracleHistoryBar
